import { React, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Box, useTheme } from "@mui/system";
import { useMutation, useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { CheckCircle } from "@mui/icons-material";
import { LocalizationProvider, MobileTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";

import AppButton from "../../../../../base/AppButton.component";
import { InputValidation } from "../../../../../../utils/InputValidationRegex";
import { DialogStyle } from "./Dialog.styles";
import { MANAGE_DEALERSHIP } from "../../../../../../graphql/mutation/mutation";
import { FETCH_MAKES } from "../../../../../../graphql/query/query";
import noImage from "../../../../../../assets/images/no-image.jpg";
import { states } from "../../../../../../utils/StatesList.utils";
import { TYPE, weekDays } from "../../../../../../utils/Utils";
import HocPopup from "../../../../../../hoc/hocPopup";
import ImageCropper from "../../../../../../utils/cropImage";
import { useAuth } from "../../../../../../context/Auth.context";

const AddDealershipDialog = (props) => {
  const theme = useTheme();
  const params = useParams();
  const auth = useAuth();
  const { data, edit = false, res, dialog } = props;
  const [selectedImage, setSelectedImage] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const { data: makesData, loading: makesLoading } = useQuery(FETCH_MAKES, {
    fetchPolicy: "network-only",
  });
  const brandNameOptions = makesData?.fetchMakes?.makeArray || [];

  const [dealer, setDealer] = useState({
    dealershipName: "",
    brandName: "",
    firstName: "",
    lastName: "",
    logoUrl: "",
    isActive: "",
    percentage: "30",
    logout_time: 30,
    verification_workflow_id: 1,
    address: {
      poBox: "",
      street: "",
      city: "",
      state: "",
      zipcode: "",
    },
    contact: {
      email: "",
      phoneNumber: "",
    },
    mainContact: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
    },
    subscription: {
      cost: "0",
      limit: "0",
      overage: "0",
      payload: 2,
      quote_cost: "0",
    },
    dealershipTiming:
      weekDays.map((day) => ({
        id: "",
        day,
        opens_at: "",
        closes_at: "",
        isclosed: false,
      })) || [],
  });
  const parseTimeStringToDate = (timeString) => {
    if (typeof timeString === "string") {
      const [hours, minutes] = timeString.split(":");
      const date = new Date();
      date.setHours(hours);
      date.setMinutes(minutes);
      return date;
    }
    return null;
  };
  const formatTimeToString = (date) => {
    return format(date, "HH:mm");
  };
  const handleStartTimeChange = (index, newTime) => {
    const updatedOpenHour = [...dealer.dealershipTiming];

    updatedOpenHour[index].opens_at = formatTimeToString(newTime);
    console.log("Temp newTime:", updatedOpenHour);

    setDealer((prev) => ({
      ...prev,
      dealershipTiming: updatedOpenHour,
    }));
  };

  const handleEndTimeChange = (index, newTime) => {
    const updatedOpenHour = [...dealer.dealershipTiming];
    updatedOpenHour[index].closes_at = formatTimeToString(newTime);
    setDealer((prev) => ({
      ...prev,
      dealershipTiming: updatedOpenHour,
    }));
  };

  const handleCheckboxChange = (index) => {
    const updatedOpenHour = [...dealer.dealershipTiming];
    updatedOpenHour[index].isclosed = !updatedOpenHour[index].isclosed;
    setDealer((prev) => ({
      ...prev,
      dealershipTiming: updatedOpenHour,
    }));
  };

  useEffect(() => {
    if (data) {
      setDealer({
        dealershipName: data?.full_name,
        brandName: data?.brand_name || "",
        firstName: data?.first_name,
        lastName: data?.last_name,
        logoUrl: data?.logo_url,
        isActive: data?.is_active,
        percentage: data?.percentage,
        logout_time: data?.logout_time,
        verification_workflow_id: data?.verification_workflow_id,
        address: {
          poBox: data?.number || "",
          street: data?.street || "",
          city: data?.city || "",
          state: data?.state || "",
          zipcode: data?.zipcode || "",
        },
        contact: {
          email: data?.email || "",
          phoneNumber: data?.phone_number || "",
        },
        mainContact: {
          firstName: data?.main_contact?.first_name || "",
          lastName: data?.main_contact?.last_name || "",
          email: data?.main_contact?.email || "",
          phoneNumber: data?.main_contact?.phone_number || "",
        },
        subscription: {
          cost: data?.subscriptions[0]?.cost,
          limit: data?.subscriptions[0]?.limit,
          overage: data?.subscriptions[0]?.overage_cost,
          payload: data?.subscriptions[0]?.payload,
          quote_cost: data?.subscriptions[0]?.quote_cost,
        },
        dealershipTiming:
          data?.dealershipTiming?.length > 0
            ? data.dealershipTiming.map((timing) => ({
                id: parseInt(timing?.id),
                day: timing?.day,
                opens_at: timing?.opens_at,
                closes_at: timing?.closes_at,
                isclosed: timing?.isclosed,
              }))
            : [],
      });
    }
  }, [data]);

  const [manageDealership, { loading: updateLoading, data: Dealership }] =
    useMutation(MANAGE_DEALERSHIP);

  useEffect(() => {
    if (Dealership) {
      if (edit) {
        setDealer({
          dealershipName: "",
          brandName: "",
          firstName: "",
          lastName: "",
          logoUrl: "",
          isActive: "",
          percentage: "30",
          logout_time: 30,
          verification_workflow_id: 1,
          address: {
            poBox: "",
            street: "",
            city: "",
            state: "",
            zipcode: "",
          },
          contact: {
            email: "",
            phoneNumber: "",
          },
          mainContact: {
            firstName: "",
            lastName: "",
            email: "",
            phoneNumber: "",
          },
          subscription: {
            cost: "0",
            limit: "0",
            overage: "0",
            payload: 2,
            quote_cost: "0",
          },
          dealershipTiming:
            weekDays.map((day) => ({
              id: "",
              day,
              opens_at: "",
              closes_at: "",
              isclosed: false,
            })) || [],
        });
      } else {
        console.log("Create dealership ", Dealership);
      }
    }
  }, [Dealership, edit]);

  const editDealerHandler = () => {
    manageDealership({
      variables: {
        dealershipInput: {
          main_contact: {
            first_name: dealer?.mainContact?.firstName,
            last_name: dealer.mainContact.lastName,
            email: dealer.mainContact.email,
            phone_number: dealer.mainContact.phoneNumber,
          },
          subscription: {
            dealership_id: params.dealershipId,
            cost: dealer.subscription.cost,
            limit: dealer.subscription.limit,
            overage_cost: dealer.subscription.overage,
            payload: dealer.subscription.payload,
            quote_cost: dealer?.subscription?.quote_cost,
          },
          dealership_id: params.dealershipId,
          dealership_name: dealer.dealershipName,
          brand_name: dealer.brandName,
          is_active: dealer.is_active,
          logo_url: selectedImage,
          role: "dealership",
          email: dealer.contact.email,
          phone_number: dealer.contact.phoneNumber,
          number: dealer.address.poBox,
          street: dealer.address.street,
          city: dealer.address.city,
          state: dealer.address.state,
          zipcode: dealer.address.zipcode,
          percentage: dealer?.percentage,
          verification_workflow_id: dealer?.verification_workflow_id,
          logout_time: dealer?.logout_time,
          created_by: auth?.user?.user_id,
          updated_by: auth?.user?.user_id,
          dealershipTimings: dealer.dealershipTiming,
        },
      },
    }).then(() => {
      showSuccess();
    });
  };

  const handleStateChange = (event, newValue) => {
    if (newValue) {
      setDealer({
        ...dealer,
        address: {
          ...dealer.address,
          state: newValue.label,
        },
      });
    }
  };
  const showSuccess = () => {
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 2000);
  };

  const options = Array.from({ length: 50 }, (_, index) =>
    (index + 1).toString()
  );

  const logoutoptions = Array.from({ length: 26 }, (_, index) => index + 5);

  const inputRef = useRef();

  const [image, setImage] = useState("");

  const handleClose = () => {
    setImage("");
    setIsOpen(false);
  };
  const onImageSelected = (selectedImg) => {
    setImage(selectedImg);
    setIsOpen(true);
  };
  useEffect(() => {
    dialog({
      open: isOpen,
      bodyType: TYPE.UPLOAD_LOGO,
      body: (
        <ImageCropper
          image={image}
          onCropDone={onCropDone}
          onCropCancel={onCropCancel}
        />
      ),
      maxWidth: "md",
    });
  }, [image]);

  const onCropCancel = () => {
    handleClose();
  };
  const onCropDone = (image) => {
    setSelectedImage(image);
    handleClose();
  };
  const handleOnChange = (event) => {
    setImage("");

    if (event.target.files && event.target.files.length > 0) {
      const reader = new FileReader();
      reader.readAsDataURL(event.target.files[0]);
      reader.onload = function (e) {
        onImageSelected(reader.result);
      };
    } else {
      setSelectedImage(null);
      handleClose();
    }
  };
  const onChooseImg = () => {
    if (inputRef.current) {
      inputRef.current.value = null;
    }
    inputRef.current.click();
  };
  return (
    <Box overflow={"scroll"} width={"100%"} height={"100%"}>
      <Box p="8px">
        <Box sx={{ pb: "16px" }}>
          <Typography variant="h6">Dealership Details</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel
              color={
                InputValidation.nameValidation(dealer.dealershipName)
                  ? "error"
                  : "primary"
              }
            >
              Dealership Name
            </InputLabel>
            <OutlinedInput
              required
              label="Dealership Name"
              value={dealer.dealershipName}
              error={InputValidation.nameValidation(dealer.dealershipName)}
              onChange={(event) =>
                setDealer({ ...dealer, dealershipName: event.target.value })
              }
            />
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {InputValidation.nameValidation(dealer.dealershipName)
                ? "Please enter a valid name"
                : " "}
            </FormHelperText>
          </FormControl>

          <FormControl variant="outlined" sx={DialogStyle.width}>
            <Autocomplete
              options={brandNameOptions}
              value={dealer.brandName || null}
              loading={makesLoading}
              disabled={makesLoading}
              onChange={(event, newValue) =>
                setDealer({ ...dealer, brandName: newValue || "" })
              }
              isOptionEqualToValue={(option, value) => option === value}
              renderInput={(params) => (
                <TextField {...params} label="Brand Name" />
              )}
            />
            <FormHelperText> </FormHelperText>
          </FormControl>
        </Box>
        <Box sx={DialogStyle.emailWidth}>
          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel
              color={
                InputValidation.addressValidation(dealer.address.poBox)
                  ? "error"
                  : "primary"
              }
            >
              P.O. Box
            </InputLabel>
            <OutlinedInput
              required
              label="P.O. Box"
              value={dealer.address.poBox}
              error={InputValidation.addressValidation(dealer.address.poBox)}
              onChange={(event) =>
                setDealer({
                  ...dealer,
                  address: { ...dealer.address, poBox: event.target.value },
                })
              }
            />
            <FormHelperText
              sx={{ color: theme.palette.error.main }}
            ></FormHelperText>
          </FormControl>
          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel color="primary">Street</InputLabel>
            <OutlinedInput
              required
              label="Street"
              error={InputValidation.addressValidation(dealer.address.street)}
              value={dealer.address.street}
              onChange={(event) =>
                setDealer({
                  ...dealer,
                  address: { ...dealer.address, street: event.target.value },
                })
              }
            />
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {InputValidation.addressValidation(dealer.address.street)
                ? "Please enter a valid street"
                : " "}
            </FormHelperText>
          </FormControl>
          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel
              color={
                InputValidation.addressValidation(dealer.address.city)
                  ? "error"
                  : "primary"
              }
            >
              City
            </InputLabel>
            <OutlinedInput
              required
              label="City"
              value={dealer.address.city}
              error={InputValidation.addressValidation(dealer.address.city)}
              onChange={(event) =>
                setDealer({
                  ...dealer,
                  address: { ...dealer.address, city: event.target.value },
                })
              }
            />
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {InputValidation.addressValidation(dealer.address.city)
                ? "Please enter a valid city"
                : " "}
            </FormHelperText>
          </FormControl>
          <FormControl variant="outlined" sx={DialogStyle.width}>
            <Autocomplete
              options={states}
              value={dealer.address.state}
              onChange={handleStateChange}
              renderInput={(params) => {
                return <TextField {...params} label="State" />;
              }}
            />
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {InputValidation.addressValidation(dealer.address.state)
                ? "Please enter a valid state"
                : " "}
            </FormHelperText>
          </FormControl>
          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel
              color={
                InputValidation.zipCodeValidation(dealer.address.zipcode)
                  ? "error"
                  : "primary"
              }
            >
              Zip Code
            </InputLabel>
            <OutlinedInput
              required
              label="Zip Code"
              type="number"
              value={dealer.address.zipcode}
              error={InputValidation.zipCodeValidation(dealer.address.zipcode)}
              onChange={(event) =>
                setDealer({
                  ...dealer,
                  address: { ...dealer.address, zipcode: event.target.value },
                })
              }
            />
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {InputValidation.zipCodeValidation(dealer.address.zipcode)
                ? "Please enter a valid zipcode"
                : " "}
            </FormHelperText>
          </FormControl>
        </Box>
        <Box sx={{ ...DialogStyle.emailWidth, marginTop: "24px" }}>
          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel
              color={
                InputValidation.emailValidation(dealer.contact.email)
                  ? "error"
                  : "primary"
              }
            >
              Email
            </InputLabel>
            <OutlinedInput
              required
              label="Email"
              type="email"
              value={dealer.contact.email}
              onChange={(event) =>
                setDealer({
                  ...dealer,
                  contact: {
                    ...dealer.contact,
                    email: event.target.value,
                  },
                })
              }
              error={InputValidation.emailValidation(dealer.contact.email)}
            />
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {InputValidation.emailValidation(dealer.contact.email)
                ? "Please enter a valid email"
                : " "}
            </FormHelperText>
          </FormControl>
          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel
              color={
                InputValidation.numberValidation(dealer.contact.phoneNumber)
                  ? "error"
                  : "primary"
              }
            >
              Contact Number
            </InputLabel>
            <OutlinedInput
              label="Contact Number"
              value={InputValidation.phoneNumberFormat(
                dealer.contact.phoneNumber
              )}
              type="tel"
              onChange={(event) =>
                setDealer({
                  ...dealer,
                  contact: {
                    ...dealer.contact,
                    phoneNumber: event.target.value,
                  },
                })
              }
              error={InputValidation.numberValidation(
                dealer.contact.phoneNumber
              )}
            />
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {InputValidation.numberValidation(dealer.contact.phoneNumber)
                ? "Please enter a valid contact number"
                : " "}
            </FormHelperText>
          </FormControl>
        </Box>
        <Box
          sx={{
            ...DialogStyle.emailWidth,
            marginTop: "16px",
          }}
        >
          <Switch
            sx={{ mt: 3 }}
            size="small"
            // onChange={crmSwitchHandle}
          />
          <Typography sx={{ width: "40%", mt: 3 }}>
            Referral Followup
          </Typography>
          <FormControl variant="outlined" sx={DialogStyle.textFieldStyles}>
            <Autocomplete
              options={logoutoptions}
              getOptionLabel={(option) => option}
              isOptionEqualToValue={(option, value) => option === value}
              value={dealer?.logout_time}
              onChange={(e, newValue) => {
                setDealer({
                  ...dealer,
                  logout_time: newValue,
                });
              }}
              renderInput={(params) => {
                return <TextField {...params} label="Referral Followup" />;
              }}
            />
          </FormControl>
        </Box>
        <Box sx={DialogStyle.addBox}>
          <Typography variant="h6">Main Contact</Typography>
        </Box>
        <Box sx={{ ...DialogStyle.emailWidth, marginTop: "16px" }}>
          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel
              color={
                InputValidation.nameValidation(dealer.mainContact.firstName)
                  ? "error"
                  : "primary"
              }
            >
              First Name
            </InputLabel>
            <OutlinedInput
              required
              label="First Name"
              value={dealer.mainContact.firstName}
              error={InputValidation.nameValidation(
                dealer.mainContact.firstName
              )}
              onChange={(event) =>
                setDealer({
                  ...dealer,
                  mainContact: {
                    ...dealer.mainContact,
                    firstName: event.target.value,
                  },
                })
              }
            />
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {InputValidation.nameValidation(dealer.mainContact.firstName)
                ? "Please enter a valid name"
                : " "}
            </FormHelperText>
          </FormControl>

          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel
              color={
                InputValidation.nameValidation(dealer.mainContact.lastName)
                  ? "error"
                  : "primary"
              }
            >
              Last Name
            </InputLabel>
            <OutlinedInput
              required
              label="Last Name"
              value={dealer.mainContact.lastName}
              error={InputValidation.nameValidation(
                dealer.mainContact.lastName
              )}
              onChange={(event) =>
                setDealer({
                  ...dealer,
                  mainContact: {
                    ...dealer.mainContact,
                    lastName: event.target.value,
                  },
                })
              }
            />
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {InputValidation.nameValidation(dealer.mainContact.lastName)
                ? "Please enter a valid name"
                : " "}
            </FormHelperText>
          </FormControl>
        </Box>
        <Box sx={DialogStyle.emailWidth}>
          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel
              color={
                InputValidation.emailValidation(dealer.mainContact.email)
                  ? "error"
                  : "primary"
              }
            >
              Email
            </InputLabel>
            <OutlinedInput
              required
              label="Email"
              type="email"
              value={dealer.mainContact.email}
              onChange={(event) =>
                setDealer({
                  ...dealer,
                  mainContact: {
                    ...dealer.mainContact,
                    email: event.target.value,
                  },
                })
              }
              error={InputValidation.emailValidation(dealer.mainContact.email)}
            />
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {InputValidation.emailValidation(dealer.mainContact.email)
                ? "Please enter a valid email"
                : " "}
            </FormHelperText>
          </FormControl>
          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel
              color={
                InputValidation.numberValidation(dealer.mainContact.phoneNumber)
                  ? "error"
                  : "primary"
              }
            >
              Contact Number
            </InputLabel>
            <OutlinedInput
              label="Contact Number"
              value={InputValidation.phoneNumberFormat(
                dealer.mainContact.phoneNumber
              )}
              type="tel"
              onChange={(event) =>
                setDealer({
                  ...dealer,
                  mainContact: {
                    ...dealer.mainContact,
                    phoneNumber: event.target.value,
                  },
                })
              }
              error={InputValidation.numberValidation(
                dealer.mainContact.phoneNumber
              )}
            />
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {InputValidation.numberValidation(dealer.mainContact.phoneNumber)
                ? "Please enter a valid contact number"
                : " "}
            </FormHelperText>
          </FormControl>
        </Box>
        <Box sx={DialogStyle.addBox}>
          <Typography variant="h6">Subscription Details</Typography>
        </Box>
        <Box
          sx={{
            ...DialogStyle.emailWidth,
            marginTop: "16px",
          }}
        >
          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel
              color={
                InputValidation.negativeValidation(dealer.subscription.cost)
                  ? "error"
                  : "primary"
              }
            >
              Subscription Cost
            </InputLabel>
            <OutlinedInput
              required
              label="Subscription Cost"
              type="number"
              value={dealer.subscription.cost}
              error={InputValidation.negativeValidation(
                dealer.subscription.cost
              )}
              inputProps={{ min: 0 }}
              onChange={(event) =>
                setDealer({
                  ...dealer,
                  subscription: {
                    ...dealer.subscription,
                    cost: event.target.value,
                  },
                })
              }
            />
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {InputValidation.negativeValidation(dealer.subscription.cost)
                ? "Please enter a valid subscription cost"
                : " "}
            </FormHelperText>
          </FormControl>

          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel
              color={
                InputValidation.negativeValidation(dealer.subscription.limit)
                  ? "error"
                  : "primary"
              }
            >
              Max Pull
            </InputLabel>
            <OutlinedInput
              required
              label="Max Pull"
              value={dealer.subscription.limit}
              error={InputValidation.negativeValidation(
                dealer.subscription.limit
              )}
              type="number"
              onChange={(event) =>
                setDealer({
                  ...dealer,
                  subscription: {
                    ...dealer.subscription,
                    limit: event.target.value,
                  },
                })
              }
            />
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {InputValidation.negativeValidation(dealer.subscription.limit)
                ? "Please enter a valid max pull"
                : " "}
            </FormHelperText>
          </FormControl>
          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel
              color={
                InputValidation.negativeValidation(
                  dealer.subscription?.quote_cost
                )
                  ? "error"
                  : "primary"
              }
            >
              Request Quote Cost
            </InputLabel>
            <OutlinedInput
              required
              label="Request Quote Cost"
              type="number"
              value={dealer.subscription?.quote_cost}
              error={InputValidation.negativeValidation(
                dealer.subscription.quote_cost
              )}
              inputProps={{ min: 0 }}
              onChange={(event) =>
                setDealer({
                  ...dealer,
                  subscription: {
                    ...dealer.subscription,
                    quote_cost: event.target.value,
                  },
                })
              }
            />
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {InputValidation.negativeValidation(
                dealer.subscription?.quote_cost
              )
                ? "Please enter a valid quote cost"
                : " "}
            </FormHelperText>
          </FormControl>
        </Box>
        <Box sx={{ ...DialogStyle.emailWidth, marginTop: "24px" }}>
          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel
              color={
                InputValidation.negativeValidation(dealer.subscription.overage)
                  ? "error"
                  : "primary"
              }
            >
              Per Pull Overage
            </InputLabel>
            <OutlinedInput
              required
              label=" Per Pull Overage"
              value={dealer.subscription.overage}
              error={InputValidation.negativeValidation(
                dealer.subscription.overage
              )}
              type="number"
              onChange={(event) =>
                setDealer({
                  ...dealer,
                  subscription: {
                    ...dealer.subscription,
                    overage: event.target.value,
                  },
                })
              }
            />
            <FormHelperText sx={{ color: theme.palette.error.main }}>
              {InputValidation.negativeValidation(dealer.subscription.overage)
                ? "Please enter a valid pull overage"
                : " "}
            </FormHelperText>
          </FormControl>
          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel id="demo-simple-select">Payload</InputLabel>
            <Select
              labelId="demo-simple-select"
              id="demo-simple"
              value={dealer.subscription.payload}
              label="Payload"
              onChange={(event) =>
                setDealer({
                  ...dealer,
                  subscription: {
                    ...dealer.subscription,
                    payload: event.target.value,
                  },
                })
              }
            >
              <MenuItem value={1}>Data</MenuItem>
              <MenuItem value={2}>Data & Docs</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={DialogStyle.addBox}></Box>
        <Box
          sx={{
            ...DialogStyle.emailWidth,
            marginTop: "16px",
          }}
        >
          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel id="demo-simple-select-label">Workflow</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={dealer?.verification_workflow_id}
              label="Workflow"
              onChange={(e) => {
                setDealer({
                  ...dealer,
                  verification_workflow_id: e.target.value,
                });
              }}
            >
              <MenuItem value={1}>Verify & Offer Quote</MenuItem>
              <MenuItem value={2}>Verify Only</MenuItem>
              <MenuItem value={3}>Offer Quote Only</MenuItem>
            </Select>
          </FormControl>

          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel id="demo-simple-select-label"></InputLabel>
            <Autocomplete
              aria-labelledby="demo-simple-select-label"
              options={options}
              // getOptionLabel={(option) => option.toString()}
              // isOptionEqualToValue={(option, value) => option === value}
              value={dealer?.percentage}
              onChange={(e, newValue) => {
                setDealer({
                  ...dealer,
                  percentage: newValue,
                });
              }}
              renderInput={(params) => {
                return <TextField {...params} label="Percentage of Savings" />;
              }}
            />
          </FormControl>

          <FormControl variant="outlined" sx={DialogStyle.width}>
            <InputLabel id="demo-simple-select-label"></InputLabel>
            <Autocomplete
              aria-labelledby="demo-simple-select-label"
              options={logoutoptions}
              // getOptionLabel={(option) => option.toString()}
              // isOptionEqualToValue={(option, value) => option === value}
              value={dealer.logout_time}
              onChange={(e, newValue) => {
                setDealer({
                  ...dealer,
                  logout_time: newValue,
                });
              }}
              renderInput={(params) => {
                return <TextField {...params} label="Auto Logout time" />;
              }}
            />
          </FormControl>
        </Box>
        <Box sx={DialogStyle.addBox}>
          <Typography variant="h6">Opening Hours</Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
          {dealer?.dealershipTiming?.map((item, index) => (
            <Box
              key={item.day}
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "16px",
                justifyContent: "flex-start",
                alignItems: "center",
                mt: "8px",
              }}
            >
              <Box sx={{ width: "15%", justifyContent: "space-between" }}>
                <Checkbox
                  checked={!item.isclosed}
                  onChange={() => handleCheckboxChange(index)}
                />
                <span>{item.day}</span>
              </Box>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <MobileTimePicker
                  label="Starts At"
                  value={
                    item.opens_at
                      ? parseTimeStringToDate(item.opens_at)
                      : parseTimeStringToDate("09:00")
                  }
                  onChange={(newTime) => {
                    handleStartTimeChange(index, newTime);
                  }}
                  renderInput={(params) => (
                    <TextField size="small" {...params} sx={{ width: "20%" }} />
                  )}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <MobileTimePicker
                  label="Closes At"
                  value={
                    item.closes_at
                      ? parseTimeStringToDate(item.closes_at)
                      : parseTimeStringToDate("21:00")
                  }
                  onChange={(newTime) => {
                    handleEndTimeChange(index, newTime);
                  }}
                  renderInput={(params) => (
                    <TextField size="small" {...params} sx={{ width: "20%" }} />
                  )}
                />
              </LocalizationProvider>
            </Box>
          ))}
        </Box>

        <Box sx={DialogStyle.box}>
          <img
            src={selectedImage || dealer?.logoUrl || noImage}
            width={120}
            alt="logo"
          />

          <Box>
            <input
              type="file"
              accept="image/*"
              ref={inputRef}
              onChange={(e) => {
                console.log("eventtonchange");
                handleOnChange(e);
                e.target.files = null;
              }}
              style={{ display: "none" }}
            />
            <Button
              variant="raised"
              color="primary"
              component="span"
              onClick={onChooseImg}
            >
              Upload
            </Button>
          </Box>
        </Box>
      </Box>
      <Divider />
      <Box sx={DialogStyle.button}>
        {Dealership?.manageDealership ? (
          <>
            {showSuccessMessage ? (
              <Box sx={{ display: "flex", my: "14px" }}>
                <CheckCircle color={"primary"} />
                <span
                  style={{
                    color: theme.palette.primary.main,
                    marginLeft: "4px",
                  }}
                >
                  SUCCESS
                </span>
              </Box>
            ) : (
              <AppButton
                buttonText={"update"}
                clickHandler={editDealerHandler}
                size="medium"
                variant="contained"
                sx={{ width: "30%", my: 1 }}
                disabled={
                  dealer.dealershipName === "" ||
                  dealer.subscription?.cost === "" ||
                  dealer.subscription?.limit === "" ||
                  dealer.subscription?.overage === "" ||
                  dealer.subscription?.quote_cost === "" ||
                  InputValidation.nameValidation(dealer.dealershipName) ||
                  InputValidation.addressValidation(dealer.address.city) ||
                  InputValidation.nameValidation(dealer.address.state) ||
                  InputValidation.nameValidation(
                    dealer.mainContact.firstName
                  ) ||
                  InputValidation.nameValidation(dealer.mainContact.lastName) ||
                  InputValidation.emailValidation(dealer.contact.email) ||
                  InputValidation.emailValidation(dealer.mainContact.email) ||
                  InputValidation.numberValidation(
                    dealer.contact.phoneNumber
                  ) ||
                  InputValidation.numberValidation(
                    dealer.mainContact.phoneNumber
                  ) ||
                  InputValidation.zipCodeValidation(dealer.address.zipcode) ||
                  InputValidation.negativeValidation(
                    dealer.subscription.cost
                  ) ||
                  InputValidation.negativeValidation(
                    dealer.subscription.limit
                  ) ||
                  InputValidation.negativeValidation(
                    dealer.subscription.overage
                  ) ||
                  InputValidation.negativeValidation(
                    dealer.subscription.quote_cost
                  )
                }
                loading={updateLoading}
              />
            )}
          </>
        ) : (
          <AppButton
            buttonText={"update"}
            clickHandler={editDealerHandler}
            size="medium"
            variant="contained"
            sx={{ width: "30%", my: 1 }}
            disabled={
              dealer.dealershipName === "" ||
              dealer.subscription?.cost === "" ||
              dealer.subscription?.limit === "" ||
              dealer.subscription?.overage === "" ||
              dealer.subscription?.quote_cost === "" ||
              InputValidation.nameValidation(dealer.dealershipName) ||
              InputValidation.addressValidation(dealer.address.city) ||
              InputValidation.nameValidation(dealer.address.state) ||
              InputValidation.nameValidation(dealer.mainContact.firstName) ||
              InputValidation.nameValidation(dealer.mainContact.lastName) ||
              InputValidation.emailValidation(dealer.contact.email) ||
              InputValidation.emailValidation(dealer.mainContact.email) ||
              InputValidation.numberValidation(dealer.contact.phoneNumber) ||
              InputValidation.numberValidation(
                dealer.mainContact.phoneNumber
              ) ||
              InputValidation.zipCodeValidation(dealer.address.zipcode) ||
              InputValidation.negativeValidation(dealer.subscription.cost) ||
              InputValidation.negativeValidation(dealer.subscription.limit) ||
              InputValidation.negativeValidation(dealer.subscription.overage) ||
              InputValidation.negativeValidation(dealer.subscription.quote_cost)
            }
            loading={updateLoading}
          />
        )}
      </Box>
    </Box>
  );
};

export default HocPopup(AddDealershipDialog);